import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Shield, 
  Users, 
  Lock, 
  Eye, 
  FileText, 
  AlertTriangle,
  Zap,
  Database,
  CheckCircle,
  AlertCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';
import './CybersecurityRiskAssessment.css';

const CybersecurityRiskAssessment = () => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [userInfo, setUserInfo] = useState({
    email: '',
    jobTitle: '',
    companyName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const categories = [
    {
      name: "Human Risk Management",
      icon: Users,
      questions: [
        {
          id: "human1",
          text: "Do you have continuous monitoring of user behavior patterns?",
          safepulseValue: "SafePulse provides real-time behavioral biometric monitoring to detect anomalies instantly."
        },
        {
          id: "human2",
          text: "Can you detect compromised credentials before they cause damage?",
          safepulseValue: "SafePulse achieves 85-92% reduction in credential-based fraud through continuous authentication."
        },
        {
          id: "human3",
          text: "Do you have systems to identify insider threats proactively?",
          safepulseValue: "SafePulse's ML algorithms detect unusual behavior patterns that indicate insider threats."
        },
        {
          id: "human4",
          text: "Is security awareness training reinforced with behavioral analytics?",
          safepulseValue: "SafePulse provides objective data on how employees actually interact with systems."
        }
      ]
    },
    {
      name: "Authentication & Access Control",
      icon: Lock,
      questions: [
        {
          id: "auth1",
          text: "Do you use multi-factor authentication beyond passwords?",
          safepulseValue: "SafePulse adds invisible behavioral biometrics as a continuous authentication layer."
        },
        {
          id: "auth2",
          text: "Is authentication continuous rather than one-time at login?",
          safepulseValue: "SafePulse monitors every interaction, not just the initial login, providing zero-friction security."
        },
        {
          id: "auth3",
          text: "Can you detect account takeover attempts in real-time?",
          safepulseValue: "SafePulse identifies when someone other than the legitimate user is accessing an account."
        },
        {
          id: "auth4",
          text: "Do you have biometric authentication that can't be stolen or shared?",
          safepulseValue: "SafePulse creates unique digital DNA from behavior patterns that are impossible to replicate."
        }
      ]
    },
    {
      name: "Threat Detection & Response",
      icon: Eye,
      questions: [
        {
          id: "threat1",
          text: "Do you have real-time threat detection capabilities?",
          safepulseValue: "SafePulse detects threats as they happen with Intel® optimized ML processing."
        },
        {
          id: "threat2",
          text: "Can you identify zero-day attacks based on behavioral anomalies?",
          safepulseValue: "SafePulse doesn't rely on known attack signatures - it detects abnormal behavior patterns."
        },
        {
          id: "threat3",
          text: "Do you have automated response mechanisms for detected threats?",
          safepulseValue: "SafePulse can trigger automatic session termination or step-up authentication when threats are detected."
        }
      ]
    },
    {
      name: "Data Protection & Privacy",
      icon: Database,
      questions: [
        {
          id: "data1",
          text: "Is sensitive data encrypted both at rest and in transit?",
          safepulseValue: "SafePulse stores all biometric data locally with encryption, never in the cloud."
        },
        {
          id: "data2",
          text: "Do you have data loss prevention (DLP) systems in place?",
          safepulseValue: "SafePulse can detect unusual data access patterns that indicate potential data exfiltration."
        },
        {
          id: "data3",
          text: "Are you compliant with GDPR, HIPAA, or other relevant regulations?",
          safepulseValue: "SafePulse's local-first architecture helps meet stringent data privacy requirements."
        }
      ]
    },
    {
      name: "Incident Response & Recovery",
      icon: AlertTriangle,
      questions: [
        {
          id: "incident1",
          text: "Do you have a documented incident response plan?",
          safepulseValue: "SafePulse provides detailed forensic data to accelerate incident investigation."
        },
        {
          id: "incident2",
          text: "Can you quickly identify the scope and impact of a security breach?",
          safepulseValue: "SafePulse's behavioral timeline shows exactly what actions were taken during an incident."
        },
        {
          id: "incident3",
          text: "Do you conduct regular security incident simulations?",
          safepulseValue: "SafePulse data can be used to create realistic attack scenarios for training."
        }
      ]
    },
    {
      name: "Compliance & Governance",
      icon: FileText,
      questions: [
        {
          id: "compliance1",
          text: "Do you maintain comprehensive audit logs of user activities?",
          safepulseValue: "SafePulse provides immutable behavioral audit trails for compliance reporting."
        },
        {
          id: "compliance2",
          text: "Are security policies enforced automatically rather than manually?",
          safepulseValue: "SafePulse enforces continuous authentication policies without user intervention."
        },
        {
          id: "compliance3",
          text: "Do you have regular third-party security audits?",
          safepulseValue: "SafePulse data provides objective evidence of security controls for auditors."
        }
      ]
    },
    {
      name: "Security Infrastructure",
      icon: Shield,
      questions: [
        {
          id: "infra1",
          text: "Do you have enterprise-grade security tools integrated across your stack?",
          safepulseValue: "SafePulse integrates seamlessly with existing SIEM, IAM, and security platforms."
        },
        {
          id: "infra2",
          text: "Is your security infrastructure scalable for growth?",
          safepulseValue: "SafePulse scales from small teams to enterprise deployments with 60% lower IT costs."
        },
        {
          id: "infra3",
          text: "Do you use AI/ML for security analytics?",
          safepulseValue: "SafePulse uses Intel® optimized ML for lightning-fast behavioral analysis."
        }
      ]
    },
    {
      name: "Cost & ROI",
      icon: Zap,
      questions: [
        {
          id: "cost1",
          text: "Have you calculated the total cost of security breaches to your organization?",
          safepulseValue: "SafePulse reduces breach costs by preventing 85-92% of credential-based attacks."
        },
        {
          id: "cost2",
          text: "Do you track ROI on security investments?",
          safepulseValue: "SafePulse delivers 60% lower IT costs and 25-40% insurance premium savings."
        }
      ]
    }
  ];

  // Initialize all answers to 0 on mount
  const [answers, setAnswers] = useState(() => {
    const initialAnswers = {};
    categories.forEach(category => {
      category.questions.forEach(question => {
        initialAnswers[question.id] = 0;
      });
    });
    return initialAnswers;
  });

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateResults = () => {
    const totalQuestions = categories.reduce((sum, cat) => sum + cat.questions.length, 0);
    const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
    const maxScore = totalQuestions * 10;
    const percentage = (totalScore / maxScore) * 100;

    let riskLevel, riskColor, riskIcon, riskDescription;
    
    if (percentage >= 80) {
      riskLevel = "Excellent Security";
      riskColor = "#34C759";
      riskIcon = CheckCircle;
      riskDescription = "Your organization demonstrates strong cybersecurity practices. Consider SafePulse to further enhance behavioral security and reduce friction.";
    } else if (percentage >= 60) {
      riskLevel = "Moderate Risk";
      riskColor = "#FFD60A";
      riskIcon = AlertTriangle;
      riskDescription = "Security gaps exist that could be exploited. SafePulse's continuous authentication can address many of these vulnerabilities.";
    } else if (percentage >= 40) {
      riskLevel = "High Risk";
      riskColor = "#FF9500";
      riskIcon = AlertCircle;
      riskDescription = "Significant security vulnerabilities detected. Your organization is at high risk of credential-based attacks. SafePulse implementation recommended.";
    } else {
      riskLevel = "Critical Risk";
      riskColor = "#FF3B30";
      riskIcon = XCircle;
      riskDescription = "Critical security gaps identified. Immediate action required. SafePulse can provide 85-92% reduction in credential-based fraud.";
    }

    return {
      totalScore,
      maxScore,
      percentage: percentage.toFixed(1),
      riskLevel,
      riskColor,
      riskIcon,
      riskDescription
    };
  };

  const handleSubmit = () => {
    setShowUserForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUserInfoChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendResults = async () => {
    if (!userInfo.email || !userInfo.jobTitle || !userInfo.companyName) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const results = calculateResults();
      
      const response = await fetch('/api/send-assessment-results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assessmentType: 'Cybersecurity Risk Assessment',
          userInfo,
          results: {
            totalScore: results.totalScore,
            maxScore: results.maxScore,
            percentage: results.percentage,
            riskLevel: results.riskLevel,
            riskDescription: results.riskDescription
          }
        })
      });

      if (response.ok) {
        setShowUserForm(false);
        setShowSuccess(true);
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else {
        alert('Failed to send results. Please try again.');
      }
    } catch (error) {
      console.error('Error sending results:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAssessment = () => {
    const initialAnswers = {};
    categories.forEach(category => {
      category.questions.forEach(question => {
        initialAnswers[question.id] = 0;
      });
    });
    setAnswers(initialAnswers);
    setShowUserForm(false);
    setUserInfo({ email: '', jobTitle: '', companyName: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="cyber-assessment-container">
      <Helmet>
        <title>Cybersecurity Risk Assessment - Erasys</title>
        <meta name="description" content="Comprehensive cybersecurity risk assessment. Identify security gaps and opportunities for SafePulse behavioral authentication integration." />
      </Helmet>

      {/* Hero Section */}
      <section className="cyber-assessment-hero">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Cybersecurity Risk Assessment</h1>
          <p>Evaluate your organization's security posture across 8 critical categories</p>
          <div className="cyber-assessment-stats">
            <div className="cyber-stat-badge">
              <span className="cyber-stat-number">26</span>
              <span className="cyber-stat-label">Questions</span>
            </div>
            <div className="cyber-stat-badge">
              <span className="cyber-stat-number">8</span>
              <span className="cyber-stat-label">Categories</span>
            </div>
            <div className="cyber-stat-badge">
              <span className="cyber-stat-number">260</span>
              <span className="cyber-stat-label">Max Points</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Success Message */}
      {showSuccess && (
        <motion.section 
          className="cyber-success-section"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="cyber-success-card">
            <div className="cyber-success-icon">
              <CheckCircle size={64} />
            </div>
            <h2>Thank You!</h2>
            <p>Your assessment results have been sent to your email.</p>
            <p className="cyber-success-redirect">Redirecting to homepage...</p>
          </div>
        </motion.section>
      )}

      {/* User Info Form */}
      {showUserForm && !showSuccess && (
        <motion.section 
          className="cyber-user-form-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="cyber-user-form-card">
            <h2>Get Your Assessment Results</h2>
            <p>Please provide your information to receive your detailed cybersecurity risk assessment results via email.</p>
            
            <div className="cyber-form-fields">
              <div className="cyber-form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={userInfo.email}
                  onChange={(e) => handleUserInfoChange('email', e.target.value)}
                  placeholder="your.email@company.com"
                  required
                />
              </div>

              <div className="cyber-form-group">
                <label htmlFor="jobTitle">Job Title *</label>
                <input
                  type="text"
                  id="jobTitle"
                  value={userInfo.jobTitle}
                  onChange={(e) => handleUserInfoChange('jobTitle', e.target.value)}
                  placeholder="e.g., IT Security Manager"
                  required
                />
              </div>

              <div className="cyber-form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  type="text"
                  id="companyName"
                  value={userInfo.companyName}
                  onChange={(e) => handleUserInfoChange('companyName', e.target.value)}
                  placeholder="Your Company Name"
                  required
                />
              </div>
            </div>

            <button 
              className="cyber-btn-submit-info" 
              onClick={handleSendResults}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Results to Email'}
              {!isSubmitting && <ArrowRight size={20} />}
            </button>
          </div>
        </motion.section>
      )}

      {/* Assessment Form */}
      {!showUserForm && (
        <section className="cyber-assessment-form">
          {categories.map((category, catIndex) => {
            const CategoryIcon = category.icon;
            return (
              <motion.div
                key={catIndex}
                className="cyber-category-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: catIndex * 0.1 }}
              >
                <div className="cyber-category-header">
                  <CategoryIcon size={32} />
                  <h2>{category.name}</h2>
                  <span className="cyber-question-count">{category.questions.length} questions</span>
                </div>

                {category.questions.map((question, qIndex) => (
                  <div key={question.id} className="cyber-question-card">
                    <p className="cyber-question-text">
                      <span className="cyber-question-number">{qIndex + 1}.</span>
                      {question.text}
                    </p>
                    
                    <div className="cyber-slider-container">
                      <div className="cyber-slider-labels">
                        <span>No (0)</span>
                        <span>Partially (5)</span>
                        <span>Yes (10)</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={answers[question.id] || 0}
                        onChange={(e) => handleAnswer(question.id, parseInt(e.target.value))}
                        className="cyber-slider"
                      />
                      <div className="cyber-slider-value">
                        Score: <strong>{answers[question.id] || 0}</strong>/10
                      </div>
                    </div>

                    <div className="safepulse-value">
                      <Shield size={16} />
                      <span><strong>SafePulse Value:</strong> {question.safepulseValue}</span>
                    </div>
                  </div>
                ))}
              </motion.div>
            );
          })}

          <motion.div
            className="cyber-submit-section"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <button className="cyber-btn-submit" onClick={handleSubmit}>
              <span>Calculate Risk Score</span>
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </section>
      )}

      {/* Info Section */}
      <section className="cyber-info-section">
        <h2>Risk Calculation Methodology</h2>
        <div className="cyber-methodology-grid">
          <div className="cyber-method-card">
            <h3>Scoring System</h3>
            <ul>
              <li>Each question: 0-10 points</li>
              <li>0 = highest risk</li>
              <li>10 = best practice</li>
              <li><strong>Total Maximum: 260 points</strong></li>
            </ul>
          </div>
          
          <div className="cyber-method-card">
            <h3>Risk Levels</h3>
            <div className="cyber-risk-level-item" style={{ borderLeftColor: '#34C759' }}>
              <strong>80-100%</strong> - Excellent Security
            </div>
            <div className="cyber-risk-level-item" style={{ borderLeftColor: '#FFD60A' }}>
              <strong>60-79%</strong> - Moderate Risk
            </div>
            <div className="cyber-risk-level-item" style={{ borderLeftColor: '#FF9500' }}>
              <strong>40-59%</strong> - High Risk
            </div>
            <div className="cyber-risk-level-item" style={{ borderLeftColor: '#FF3B30' }}>
              <strong>0-39%</strong> - Critical Risk
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CybersecurityRiskAssessment;
